import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import laserzoneLogo from '../assets/logos/laserzone.webp';
import funzoneLogoWebp from '../assets/logos/funzone.webp';
import axezoneLogo from '../assets/logos/axezone.webp';
import exitzoneLogo from '../assets/logos/exitzone.webp';
import golfzoneLogo from '../assets/logos/golfzone.webp';
import bashzoneLogo from '../assets/logos/bashzone.webp';
import paintzoneLogo from '../assets/logos/paintzone.webp';
import pixelzoneLogo from '../assets/logos/pixelzone.webp';
import rallyezoneLogo from '../assets/logos/rallyezone.webp';
import karaokezoneLogo from '../assets/logos/karaokezone.webp';
import { useAuth } from './authContext';

const EntertainmentContext = createContext();

// Mapping between group paths and entertainment types
const groupPathToEntertainment = {
  Lasertag: 'LaserZone',
  Axtwerfen: 'AxeZone',
  // Add more mappings as needed
};

// Mapping between route paths and entertainment types (for direct route matching)
const routeToEntertainment = {
  '/briefings': 'LaserZone',
  '/teamAssignment': 'LaserZone',
  '/operator': 'LaserZone',
  '/lasertagStats': 'LaserZone',
  '/memberCardQueue': 'LaserZone',
  '/waivers': 'AxeZone',
};

export const useEntertainment = () => {
  const context = useContext(EntertainmentContext);
  if (!context) {
    throw new Error('useEntertainment must be used within an EntertainmentProvider');
  }
  return context;
};

// Component that handles route-based entertainment detection (must be inside Router context)
const EntertainmentRouteDetector = ({ children }) => {
  const { isCounterDevice, menuItems } = useAuth();
  const { setCurrentEntertainment } = useEntertainment();
  const location = useLocation();

  // Automatically detect entertainment based on current route (only for counter devices)
  useEffect(() => {
    if (!isCounterDevice) {
      return;
    }

    const currentPath = location.pathname;

    // First check direct route mapping
    if (routeToEntertainment[currentPath]) {
      setCurrentEntertainment(routeToEntertainment[currentPath]);
      return;
    }

    // Then check menu items for group path mapping
    const currentMenuItem = menuItems.find(item => item.link === currentPath);
    if (currentMenuItem && currentMenuItem.groupPath) {
      const entertainment = groupPathToEntertainment[currentMenuItem.groupPath];
      if (entertainment) {
        setCurrentEntertainment(entertainment);
        return;
      }
    }

    // If no specific entertainment found, reset to null
    setCurrentEntertainment(null);
  }, [location.pathname, isCounterDevice, menuItems, setCurrentEntertainment]);

  return children;
};

export const EntertainmentProvider = ({ children }) => {
  const [currentEntertainment, setCurrentEntertainment] = useState(null);
  const [currentView, setCurrentView] = useState('default');
  const { isCounterDevice } = useAuth();

  const getLogoForEntertainment = entertainmentName => {
    switch (entertainmentName) {
      case 'LaserZone':
        return laserzoneLogo;
      case 'AxeZone':
        return axezoneLogo;
      case 'ExitZone':
        return exitzoneLogo;
      case 'GlowZone':
        return golfzoneLogo;
      case 'BashZone':
        return bashzoneLogo;
      case 'PaintballZone':
        return paintzoneLogo;
      case 'PixelZone':
        return pixelzoneLogo;
      case 'RallyeZone':
        return rallyezoneLogo;
      case 'KaraokeZone':
        return karaokezoneLogo;
      case 'FunZone':
        return funzoneLogoWebp;
      default:
        return funzoneLogoWebp;
    }
  };

  const currentLogo = useMemo(() => {
    // Only change logo on counter devices
    if (isCounterDevice && currentEntertainment) {
      // Show entertainment-specific logo if we're in bookings view OR if entertainment was auto-detected from route
      if (currentView === 'bookings' || currentEntertainment) {
        return getLogoForEntertainment(currentEntertainment);
      }
    }
    return funzoneLogoWebp;
  }, [isCounterDevice, currentView, currentEntertainment]);

  const value = {
    currentEntertainment,
    setCurrentEntertainment,
    currentView,
    setCurrentView,
    currentLogo,
  };

  return <EntertainmentContext.Provider value={value}>{children}</EntertainmentContext.Provider>;
};

// Export the route detector component to be used inside Router context
export { EntertainmentRouteDetector };
